<template>
  <div class="container">
    <div class="header">
      <span class="tag">语音转文字</span>
    </div>

    <div class="two-column">
      <div class="left-column">
        <div class="audio-wrapper">
          <audio ref="audioPlayer" :src="audioSrc" @timeupdate="updateProgress" @loadedmetadata="updateDuration" @play="isPlaying = true" @pause="isPlaying = false" @ended="onAudioEnded"/>

          <div class="progress-wrapper" @click="seekAudio">
            <div class="progress-bar">
              <div class="wave-bg" :class="{ 'wave-animate': isPlaying }">
                <svg class="wave" viewBox="0 0 200 20" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="waveBgGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stop-color="#33BEA7" stop-opacity="0.15" />
                      <stop offset="50%" stop-color="#33BEA7" stop-opacity="0.25" />
                      <stop offset="100%" stop-color="#33BEA7" stop-opacity="0.15" />
                    </linearGradient>
                  </defs>
                  <path d="M0,10 L5,8 L10,12 L15,8 L20,12 L25,8 L30,12 L35,8 L40,12 L45,8 L50,12 L55,8 L60,12 L65,8 L70,12 L75,8 L80,12 L85,8 L90,12 L95,8 L100,12 L105,8 L110,12 L115,8 L120,12 L125,8 L130,12 L135,8 L140,12 L145,8 L150,12 L155,8 L160,12 L165,8 L170,12 L175,8 L180,12 L185,8 L190,12 L195,8 L200,10 L200,20 L0,20 Z" fill="url(#waveBgGradient)" />
                </svg>
              </div>
              <div class="progress-fill" :style="{ width: progressPercent + '%' }">
                <div class="wave-container" :class="{ 'wave-animate': isPlaying }">
                  <svg class="wave" viewBox="0 0 200 20" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stop-color="#7EC8A3" stop-opacity="0.9" />
                        <stop offset="50%" stop-color="#99D9B5" stop-opacity="1" />
                        <stop offset="100%" stop-color="#7EC8A3" stop-opacity="0.9" />
                      </linearGradient>
                    </defs>
                    <path d="M0,10 L5,8 L10,12 L15,8 L20,12 L25,8 L30,12 L35,8 L40,12 L45,8 L50,12 L55,8 L60,12 L65,8 L70,12 L75,8 L80,12 L85,8 L90,12 L95,8 L100,12 L105,8 L110,12 L115,8 L120,12 L125,8 L130,12 L135,8 L140,12 L145,8 L150,12 L155,8 L160,12 L165,8 L170,12 L175,8 L180,12 L185,8 L190,12 L195,8 L200,10 L200,20 L0,20 Z" fill="url(#waveGradient)" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div class="controls-bar">
            <div class="left-controls">
              <span class="play-btn" @click="togglePlay">
                {{ isPlaying ? '❚❚' : '▶' }}
              </span>
              <span class="time-display">{{ currentTime }} / {{ duration }}</span>
            </div>
            <div class="right-controls">
              <div class="speed-control" @click.stop="showSpeedDropdown = !showSpeedDropdown">
                <span class="speed-label">倍速</span>
                <span class="speed-value">{{ speed }}x</span>
                <div class="speed-dropdown" v-show="showSpeedDropdown">
                  <div class="speed-option" v-for="s in speedOptions" :key="s" @click.stop="setSpeed(s)">
                    <span>{{ s }}x</span>
                    <span v-if="s === speed" class="check">✓</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="speaker-section" v-if="data.speakers">
          <div class="speaker" v-for="(speaker, idx) in data.speakers" :key="idx">
            <div class="speaker-header">
              <img
                :src="speaker.type == 1
                  ? require('../../../assets/admin.png')
                  : require('../../../assets/admin1.png')"
                alt="发言人头像"
                class="speaker-avatar"
              >
              <span class="speaker-name">{{ speaker.speaker_name }}</span>
              <span class="speaker-time">{{ speaker.time }}</span>
            </div>
            <div :class="speaker.type === 1 ? 'speaker-text' : 'speaker-text2'">
              {{ speaker.content }}
            </div>
          </div>
        </div>
        <div v-else>
          <el-empty description="暂无发言记录"></el-empty>
        </div>
      </div>

      <div class="right-column">
        <div>
          <h3>关键词</h3>
          <div style="display: flex; gap: 8px; flex-wrap: wrap;" v-if="data.keyword">
            <el-tag v-for="(kw, idx) in data.keyword" :key="idx">{{ kw }}</el-tag>
          </div>
          <div v-else>
            <p>暂无关键词~</p>
          </div>
        </div>


        <div class="summary">
          <h3>全文概要</h3>
          <p style="margin-bottom:40px" v-if="data.summary">{{ data.summary }}</p>
          <div v-else>
            <p>暂无全文概要~</p>
          </div>
        </div>
        <el-tabs v-model="activeName" >
          <el-tab-pane label="章节速览" name="first">
            <el-timeline :reverse="true" v-if="data.chapters">
              <el-timeline-item
                v-for="(item, index) in data.chapters"
                :key="index"
              >
                <div class="chapter-item">
                  <span>{{ item.headline }}</span>
                  <p>{{ item.summary }}</p>
                </div>
              </el-timeline-item>
            </el-timeline>
            <div v-else>
              <el-empty description="暂无章节速览"></el-empty>
            </div>
          </el-tab-pane>
          <el-tab-pane label="问答回顾" name="second">
            <el-timeline :reverse="true" v-if="data.qa">
              <el-timeline-item
                v-for="(item, index) in data.qa"
                :key="index"
              >
                <div class="qa-item">
                  <span>{{ item.question }}</span>
                  <p>{{ item.answer }}</p>
                </div>
              </el-timeline-item>
            </el-timeline>
            <div v-else>
              <el-empty description="暂无问答回顾"></el-empty>
            </div>
          </el-tab-pane>
          <el-tab-pane label="发言总结" name="third">
            <div v-if="data.speech">        
              <div v-for="(item, idx) in data.speech" :key="idx" class="speech-item">
                <div class="speech-header">
                  <img
                    :src="item.type == 1
                      ? require('../../../assets/admin.png')
                      : require('../../../assets/admin1.png')"
                    alt="发言人头像"
                    class="speaker-avatar"
                  >
                  <p>{{ item.speaker_name }}</p>
                </div>
                <p>{{ item.content }}</p>
              </div>
            </div>
            <div v-else>  
              <el-empty description="暂无发言总结"></el-empty>
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>
    </div>

    <!-- 返回顶部悬浮按钮 -->
    <div class="back-top" v-show="showBackTop" @click="scrollToTop">↑</div>
  </div>
</template>

<script>
import { getAudioContent } from '@/api/client'
export default {
  name: 'App',
  data() {
    return {
      activeName: 'first',
      audioSrc: '',
      isPlaying: false,
      currentTime: '00:00',
      duration: '00:00',
      progressPercent: 0,
      volume: 0.8,
      speed: 1,
      speedOptions: [0.5, 0.75, 1, 1.25, 1.5, 2],
      showSpeedDropdown: false,
      data: {},
      showBackTop: false,
      scrollContainer: null
    }
  },
  computed: {
    wavePaths() {
      const paths = []
      for (let i = 0; i < 5; i++) {
        const phase = (i / 4) * Math.PI * 2
        const points = []
        for (let x = 0; x <= 200; x += 5) {
          const y = 10 + 6 * Math.sin(x / 20 + phase)
          points.push(`${x},${y}`)
        }
        paths.push(`M0,10 ${points.map(p => `L${p}`).join(' ')} L200,20 L0,20 Z`)
      }
      return paths
    }
  },
  mounted() {
    this.audioSrc = this.$route.query.url
    this.audio = this.$refs.audioPlayer
    console.log(this.audioSrc)
    if (this.audio) {
      this.audio.volume = this.volume
      this.audio.playbackRate = this.speed
    }
    this.getAudioContent()
    document.addEventListener('click', this.closeSpeedDropdown)

    this.$nextTick(() => {
      setTimeout(() => {
        this.scrollContainer = this.findScrollParent(this.$el)
        if (this.scrollContainer) {
          console.log('成功获取滚动容器', this.scrollContainer)
          this.scrollContainer.addEventListener('scroll', this.handleScroll)
        } else {
          console.warn('未找到 el-scrollbar / el-main 滚动容器，降级使用 window 滚动')
          window.addEventListener('scroll', this.handleScroll)
          this.scrollContainer = window
        }
      }, 300)
    })
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeSpeedDropdown)
    if (this.scrollContainer) {
      this.scrollContainer.removeEventListener('scroll', this.handleScroll)
    }
  },
  methods: {
    findScrollParent(el) {
      if (!el || el === document.documentElement) return null
      if (el.classList.contains('el-scrollbar')) {
        const wrapDom = el.querySelector('.el-scrollbar__wrap')
        if (wrapDom) {
          return wrapDom
        }
      }
      if (el.classList.contains('el-main')) {
        return el
      }
      return this.findScrollParent(el.parentElement)
    },
    handleScroll() {
      const top = this.scrollContainer === window ? window.scrollY : this.scrollContainer.scrollTop
      this.showBackTop = top > 300
    },
    scrollToTop() {
      if (!this.scrollContainer) return
      this.scrollContainer.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    },
    getAudioContent() {
      getAudioContent({
        task_id: this.$route.query.id
      }).then(res => {
        this.data = res
      })
    },
    togglePlay() {
      if (!this.audio) return
      if (this.audio.paused) {
        this.audio.play()
      } else {
        this.audio.pause()
      }
    },
    updateProgress() {
      if (this.audio && this.audio.duration) {
        const percent = (this.audio.currentTime / this.audio.duration) * 100
        this.progressPercent = percent
        this.currentTime = this.formatTime(this.audio.currentTime)
      }
    },
    updateDuration() {
      if (this.audio && this.audio.duration) {
        this.duration = this.formatTime(this.audio.duration)
      }
    },
    onAudioEnded() {
      this.isPlaying = false
      this.progressPercent = 0
      this.currentTime = '00:00'
    },
    formatTime(seconds) {
      const min = Math.floor(seconds / 60)
      const sec = Math.floor(seconds % 60)
      return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
    },
    seekAudio(event) {
      if (!this.audio || !this.audio.duration) return
      const rect = this.$el.querySelector('.progress-bar').getBoundingClientRect()
      const clickX = event.clientX - rect.left
      const width = rect.width
      const percent = Math.min(Math.max(clickX / width, 0), 1)
      this.audio.currentTime = percent * this.audio.duration
    },
    setSpeed(s) {
      this.speed = s
      if (this.audio) {
        this.audio.playbackRate = s
      }
      this.showSpeedDropdown = false
    },
    closeSpeedDropdown() {
      this.showSpeedDropdown = false
    }
  }
}
</script>

<style scoped lang="scss">
.qa-item,.chapter-item,.speech-item{
  padding:15px 10px;
  background-color:#F8F8FA;
  span{
    display: block;
    margin-bottom: 12px;
    color:#000;
  }
}
.speech-item{
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  .speech-header{
    width: 70px;
    box-sizing: border-box;
    text-align: center;
    flex-shrink: 0;
  }
}

::v-deep .el-tabs__item {
  font-size: 16px !important;
  font-weight: 600 !important;
}
::v-deep .el-tabs__item.is-active {
  color: #333;
}
.container {
  margin: 0 auto;
  background: white;
  border-radius: 24px;
  padding: 24px 28px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.05);
}
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 16px;
  border-bottom: 1px solid #eef2f6;
}
.tag {
  background: #eef3fc;
  color: #1e5f8e;
  font-weight: 600;
  font-size: 14px;
  padding: 4px 14px;
  border-radius: 30px;
}

/* 左右两栏布局 */
.two-column {
  display: flex;
  gap: 30px;
  align-items: flex-start;
}

/* 左侧：聊天记录 */
.left-column {
  flex: 1;
  min-width: 0;
  padding-right: 8px;
}
.right-column::-webkit-scrollbar,
.left-column::-webkit-scrollbar {
  width: 4px;
}
.right-column::-webkit-scrollbar-thumb,
.left-column::-webkit-scrollbar-thumb {
  background: #d0d8e0;
  border-radius: 4px;
}

.right-column {
  flex: 1;
  min-width: 0;
  font-size: 13px;
}

h3{
  font-size: 16px;
}

p{
  margin:0;
  color: #666;
  font-size: 13px;
  line-height: 18px;
}

/* ===== 音频播放器 - 动态波浪进度条 ===== */
.audio-wrapper {
  background: transparent;
  padding: 12px 0;
  margin-bottom: 16px;
  border-bottom: 1px solid #eef2f6;
}
.audio-wrapper audio {
  display: none;
}

/* 进度条容器 */
.progress-wrapper {
  cursor: pointer;
}
.progress-bar {
  width: 100%;
  height: 25px;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
}

/* 底层静态波浪 */
.wave-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}
.wave-bg .wave {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

/* 上层播放进度 */
.progress-fill {
  position: relative;
  height: 100%;
  transition: width 0.1s linear;
  overflow: hidden;
  z-index: 1;
}
.wave-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}
.wave-container .wave {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

/* ===== 波浪动画 ===== */
.wave-bg .wave path,
.wave-container .wave path {
  transform: translateX(0);
  transition: transform 0.3s ease;
}
.wave-animate .wave path {
  animation: waveMove 2.5s ease-in-out infinite;
}
.wave-bg.wave-animate .wave path {
  animation: waveMove 3s ease-in-out infinite;
}
.wave-container.wave-animate .wave path {
  animation: waveMove 1.8s ease-in-out infinite;
}

@keyframes waveMove {
  0%, 100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-8px);
  }
  75% {
    transform: translateX(8px);
  }
}

/* 光泽效果 */
.progress-fill::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 40%;
  background: linear-gradient(180deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0) 100%);
  border-radius: 10px 10px 0 0;
  pointer-events: none;
  z-index: 2;
}

/* 底部控制栏 */
.controls-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 6px;
}
.left-controls {
  display: flex;
  align-items: center;
  gap: 14px;
}
.play-btn {
  width: 28px;
  height: 28px;
  line-height: 28px;
  border-radius: 50%;
  color: #fff;
  font-size: 12px;
  cursor: pointer;
  user-select: none;
  text-align: center;
  background: #33BEA7;
}
.play-btn:hover {
  opacity: 0.85;
}
.time-display {
  font-size: 13px;
  color: #6b7a8a;
  font-variant-numeric: tabular-nums;
}
.right-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #6b7a8a;
}
.speed-control {
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  user-select: none;
  padding: 2px 6px;
  border-radius: 4px;
  transition: background 0.2s;
}
.speed-control:hover {
  background: #f0f2f5;
}
.speed-label {
  color: #6b7a8a;
}
.speed-value {
  color: #1a2a3a;
  font-weight: 500;
}

/* 倍速下拉菜单 */
.speed-dropdown {
  position: absolute;
  bottom: -180px;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  box-shadow: 0 8px 30px rgba(0,0,0,0.15);
  padding: 6px 0;
  min-width: 80px;
  z-index: 100;
  border: 1px solid #eef2f6;
  border-radius: 8px;
}
.speed-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 16px;
  font-size: 13px;
  color: #1a2a3a;
  cursor: pointer;
  transition: background 0.15s;
}
.speed-option:hover {
  background: #f0f4fe;
}
.speed-option .check {
  color: #33BEA7;
  font-weight: 600;
}

/* 发言人样式 */
.speaker-section {
  margin: 0;
}
.speaker {
  padding: 12px 0;
}
.speaker:last-child {
  border-bottom: none;
}
.speaker-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
}
.speaker-name {
  color: #0b1c2e;
  font-size: 13px;
  font-weight: 600;
}
.speaker-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
}
.speaker-time {
  color: #6b7a8a;
  font-size: 12px;
  padding: 0 10px;
}
.speaker-text {
  color: #1e2a36;
  line-height: 1.6;
  font-size: 13px;
  padding-left: 4px;
  background: #F7F8FC;
  padding: 5px 14px;
  border-radius: 8px;
}
.speaker-text2 {
  color: #1e2a36;
  line-height: 1.6;
  font-size: 13px;
  padding-left: 4px;
  background: #FFF;
  border: 1px solid #33BEA7;
  padding: 5px 14px;
  border-radius: 8px;
}

/* 返回顶部按钮 */
.back-top {
  position: fixed;
  right: 40px;
  bottom: 40px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background-color: #33BEA7;
  color: #fff;
  text-align: center;
  line-height: 44px;
  font-size: 18px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(51, 190, 167, 0.35);
  z-index: 999;
  transition: all 0.24s ease;
  user-select: none;
  &:hover {
    background-color: #2ca895;
    transform: translateY(-4px);
    box-shadow: 0 6px 16px rgba(51, 190, 167, 0.4);
  }
}
</style>